import { useState, useEffect, useRef, useMemo } from 'react';
import { applyBoidsForces, SpatialGrid } from '@/lib/murmuration-physics';

/**
 * Configuration for murmuration physics and behaviour
 */
export interface MurmurationConfig {
  separationDistance: number;    // Collision avoidance radius (pixels)
  cohesionStrength: number;      // Center gravity pull strength (0-1)
  alignmentStrength: number;     // Murmuration sync strength (0-1)
  maxSpeed: number;              // Maximum drift velocity (pixels/frame)
  focusZoneMin: number;          // Z-depth for sharp focus start (0-100)
  focusZoneMax: number;          // Z-depth for sharp focus end (0-100)
  zDriftSpeed: number;           // How fast pills drift through z-space
}

/**
 * Default configuration creates meditative, non-distracting animation
 */
export const DEFAULT_MURMURATION_CONFIG: MurmurationConfig = {
  separationDistance: 30,
  cohesionStrength: 0.005,
  alignmentStrength: 0.15,
  maxSpeed: 1.0,
  focusZoneMin: 40,
  focusZoneMax: 60,
  zDriftSpeed: 0.01,
};

export interface PillState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  blur: number;
  opacity: number;
}

export interface InitialPosition {
  x: number;
  y: number;
}

interface UseMurmurationProps {
  pillCount: number;
  isActive: boolean;
  containerDimensions: { width: number; height: number };
  initialPositions?: InitialPosition[];
  config?: Partial<MurmurationConfig>;
  enableReducedMotion?: boolean;
}

interface UseMurmurationReturn {
  pillStates: PillState[];
  isTransitioning: boolean;
  setIsTransitioning: (value: boolean) => void;
  fps: number;
}

/**
 * Custom hook for managing murmuration animation physics
 * 
 * This hook creates a physics-based animation system where pills:
 * - Avoid collisions using boids separation force
 * - Maintain group cohesion with subtle center gravity
 * - Drift in/out of focus using z-depth
 * - Animate smoothly at 60fps using requestAnimationFrame
 * 
 * @example
 * ```tsx
 * const { pillStates, isTransitioning } = useMurmuration({
 *   pillCount: 20,
 *   isActive: isDispersed,
 *   containerDimensions: dimensions,
 * });
 * 
 * {pillStates.map((state, i) => (
 *   <div style={{
 *     transform: `translate(${state.x}px, ${state.y}px)`,
 *     filter: `blur(${state.blur}px)`,
 *     opacity: state.opacity
 *   }} />
 * ))}
 * ```
 */
export const useMurmuration = ({
  pillCount,
  isActive,
  containerDimensions,
  initialPositions = [],
  config: userConfig = {},
  enableReducedMotion = false,
}: UseMurmurationProps): UseMurmurationReturn => {
  const config = { ...DEFAULT_MURMURATION_CONFIG, ...userConfig };
  
  // Store pill states in ref to avoid triggering re-renders on every frame
  const pillStatesRef = useRef<PillState[]>([]);
  
  // Spatial grid for efficient collision detection
  const spatialGridRef = useRef<SpatialGrid>(new SpatialGrid(config.separationDistance * 2));
  
  // Trigger React re-renders periodically for smooth animation
  const [, forceUpdate] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fps, setFps] = useState(60);
  
  const animationFrameRef = useRef<number>();
  const lastFrameTimeRef = useRef<number>(0);
  const transitionStartRef = useRef<number>(0);
  const transitionDuration = 1200; // 1.2 seconds
  
  // Performance monitoring
  const frameCountRef = useRef(0);
  const lastFpsUpdateRef = useRef(0);
  
  // Initialize pill positions on mount or when count changes
  useEffect(() => {
    if (pillStatesRef.current.length !== pillCount) {
      pillStatesRef.current = Array.from({ length: pillCount }, (_, i) => {
        // Use initialPositions if provided, otherwise random position
        const hasInitialPos = initialPositions[i]?.x !== undefined;
        return {
          x: hasInitialPos ? initialPositions[i].x : Math.random() * containerDimensions.width,
          y: hasInitialPos ? initialPositions[i].y : Math.random() * containerDimensions.height,
          z: 50, // Start in focus
          vx: (Math.random() - 0.5) * config.maxSpeed,
          vy: (Math.random() - 0.5) * config.maxSpeed,
          blur: 0,
          opacity: 1,
        };
      });
    }
  }, [pillCount, containerDimensions.width, containerDimensions.height, config.maxSpeed, initialPositions]);
  
  // Handle transition start when isActive changes
  useEffect(() => {
    if (isActive) {
      // ENTERING CONSTELLATION
      setIsTransitioning(true);
      transitionStartRef.current = performance.now();
      
      // Give pills initial velocity burst without resetting positions
      pillStatesRef.current = pillStatesRef.current.map((pill) => ({
        ...pill,
        // Pills stay where they are, just get initial random velocity
        vx: (Math.random() - 0.5) * config.maxSpeed * 2,
        vy: (Math.random() - 0.5) * config.maxSpeed * 2,
      }));
      
      // End transition after duration
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
      }, transitionDuration);
      
      return () => clearTimeout(timeout);
    } else {
      // EXITING CONSTELLATION - keep isTransitioning true for smooth return
      setIsTransitioning(true);
      
      // Reset pills to their initial (flexbox) positions for smooth interpolation
      if (initialPositions.length > 0) {
        pillStatesRef.current = pillStatesRef.current.map((pill, i) => ({
          ...pill,
          x: initialPositions[i]?.x ?? pill.x,
          y: initialPositions[i]?.y ?? pill.y,
          vx: 0, // Stop velocity for transition
          vy: 0,
        }));
        forceUpdate(prev => prev + 1); // Trigger re-render with new positions
      }
      
      // End transition after duration
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
      }, transitionDuration);
      
      return () => clearTimeout(timeout);
    }
  }, [isActive, initialPositions, config.maxSpeed]);
  
  // Animation loop
  useEffect(() => {
    if (!isActive || enableReducedMotion) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }
    
    const animate = (timestamp: number) => {
      // Calculate delta time for frame-rate independent animation
      const deltaTime = timestamp - lastFrameTimeRef.current;
      lastFrameTimeRef.current = timestamp;
      
      // Performance monitoring - update FPS every second
      frameCountRef.current++;
      if (timestamp - lastFpsUpdateRef.current >= 1000) {
        setFps(frameCountRef.current);
        frameCountRef.current = 0;
        lastFpsUpdateRef.current = timestamp;
      }
      
      // Update pill states based on physics
      const states = pillStatesRef.current;
      
      // Check if we're in transition phase
      const transitionElapsed = timestamp - transitionStartRef.current;
      const inTransition = transitionElapsed < transitionDuration;
      
      // Rebuild spatial grid for efficient neighbor queries
      if (!inTransition) {
        spatialGridRef.current.rebuild(states);
      }
      
      states.forEach((pill, i) => {
        // Update z-position using sine wave for smooth in/out of focus drift
        const zTime = timestamp * 0.001 * config.zDriftSpeed;
        pill.z = 50 + Math.sin(zTime + i) * 30; // Oscillate between 20-80
        
        // Calculate blur based on z-depth (far = more blur) - softer effect
        const distanceFromFocus = Math.abs(pill.z - 50);
        pill.blur = inTransition ? 0 : Math.max(0, (distanceFromFocus / 30) * 2); // Max 2px blur
        
        // Calculate opacity based on z-depth (far = more transparent) - more visible
        pill.opacity = inTransition ? 1 : Math.max(0.7, 1 - (distanceFromFocus / 30) * 0.3);
        
        // Only update position and apply physics after transition completes
        if (!inTransition) {
          // Get nearby pills for boids calculations (performance optimization)
          const nearbyPills = spatialGridRef.current.getNearby(pill.x, pill.y);
          
          // Apply boids forces (separation, alignment, cohesion)
          applyBoidsForces(pill, nearbyPills, {
            separationDistance: config.separationDistance,
            alignmentDistance: 25,      // Reduced range
            cohesionDistance: 25,       // Reduced range
            separationWeight: 2.5,      // Much stronger separation (500x cohesion)
            alignmentWeight: config.alignmentStrength,
            cohesionWeight: config.cohesionStrength,
            centerPullStrength: 0, // Disabled - using ellipse boundary instead
            centerX: 0,
            centerY: 0,
            ellipseBoundary: {
              enabled: true,
              radiusX: containerDimensions.width * 0.35,  // 35% width (horizontal)
              radiusY: containerDimensions.height * 0.35, // 35% height (vertical)
              strength: 0.015 // Gentle boundary repulsion
            },
            maxSpeed: config.maxSpeed,
          });
          
          // Update position based on velocity
          pill.x += pill.vx;
          pill.y += pill.vy;
          
          // Boundary repulsion instead of wrapping
          const halfWidth = containerDimensions.width / 2;
          const halfHeight = containerDimensions.height / 2;
          const margin = 50; // Start repelling 50px from edge
          
          // Horizontal boundaries
          if (pill.x < -halfWidth + margin) {
            const force = (-halfWidth + margin - pill.x) * 0.01;
            pill.vx += force;
          } else if (pill.x > halfWidth - margin) {
            const force = (halfWidth - margin - pill.x) * 0.01;
            pill.vx += force;
          }
          
          // Vertical boundaries
          if (pill.y < -halfHeight + margin) {
            const force = (-halfHeight + margin - pill.y) * 0.01;
            pill.vy += force;
          } else if (pill.y > halfHeight - margin) {
            const force = (halfHeight - margin - pill.y) * 0.01;
            pill.vy += force;
          }
        }
      });
      
      // Update UI every frame for smooth 60fps animation
      forceUpdate(prev => prev + 1);
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, containerDimensions, config, transitionDuration, enableReducedMotion]);
  
  // Return current pill states
  return {
    pillStates: pillStatesRef.current,
    isTransitioning,
    setIsTransitioning,
    fps,
  };
};
